/**
 * The design's SIP.js configuration block, verbatim in option names (checked against the
 * installed 0.21.2 .d.ts by sip-options.test.ts). One UserAgent per SIP credential; a
 * credential swap builds a new one (only ever with no line up).
 */
import { Registerer, UserAgent, type RegistererOptions, type UserAgentDelegate, type UserAgentOptions, Web } from 'sip.js';
import type { LaunchResponse } from '@ccphone/contracts';
import { ccSessionDescriptionHandlerFactory } from './sdh.js';

export interface SipBootstrap {
  sip: LaunchResponse['sip'];
  turn: LaunchResponse['turn'];
  policy: Pick<LaunchResponse['policy'], 'codecs' | 'iceTransportPolicy'>;
}

export interface UserAgentDeps {
  version: string;
  delegate: UserAgentDelegate;
  mediaStreamFactory?: Web.MediaStreamFactory;
  logConnector?: UserAgentOptions['logConnector'];
}

export const TRANSPORT_OPTIONS_BASE = { connectionTimeout: 5, keepAliveInterval: 30, keepAliveDebounce: 10 } as const;
export const REGISTERER_OPTIONS = { expires: 300, refreshFrequency: 50 } satisfies RegistererOptions;
export const ICE_GATHERING_TIMEOUT_MS = 1500;
export const NO_ANSWER_TIMEOUT_S = 60;

export function sipUriFor(b: Pick<SipBootstrap, 'sip'>): string {
  return `sip:${b.sip.username}@${b.sip.domain}`;
}

export function peerConnectionConfiguration(b: Pick<SipBootstrap, 'turn' | 'policy'>): RTCConfiguration {
  return {
    iceServers: [{ urls: b.turn.urls, username: b.turn.username, credential: b.turn.credential }],
    // the tenant policy wins when it forces relay; the TURN credential carries the same flag
    iceTransportPolicy: b.turn.policy === 'relay' || b.policy.iceTransportPolicy === 'relay' ? 'relay' : 'all',
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
  };
}

export function buildUserAgentOptions(b: SipBootstrap, d: UserAgentDeps): UserAgentOptions {
  const uri = UserAgent.makeURI(sipUriFor(b));
  if (!uri) throw new Error(`invalid SIP URI for ${b.sip.username}`);
  const transportOptions: Web.TransportOptions = { server: b.sip.wss, ...TRANSPORT_OPTIONS_BASE };
  const sdhOptions: Web.SessionDescriptionHandlerFactoryOptions = {
    iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
    peerConnectionConfiguration: peerConnectionConfiguration(b),
  };
  return {
    uri,
    authorizationUsername: b.sip.username,
    authorizationPassword: b.sip.password,
    userAgentString: `CCPhone/${d.version}`,
    transportOptions,
    hackIpInContact: false, // RFC 7118 .invalid Contact; Asterisk rewrite_contact=yes
    noAnswerTimeout: NO_ANSWER_TIMEOUT_S,
    sessionDescriptionHandlerFactory: ccSessionDescriptionHandlerFactory(b.policy.codecs, d.mediaStreamFactory),
    sessionDescriptionHandlerFactoryOptions: sdhOptions,
    delegate: d.delegate,
    logLevel: 'warn',
    ...(d.logConnector ? { logConnector: d.logConnector, logBuiltinEnabled: false } : {}),
    // reconnection is owned by SipReconnector (node_state gating); sip.js must not retry on its own
    reconnectionAttempts: 0,
    // stop() must never send an un-REGISTER on its own (supersede = zero REGISTERs from the old
    // window); a normal logout calls registerer.unregister() explicitly
    gracefulShutdown: false,
  };
}

export function createUserAgent(b: SipBootstrap, d: UserAgentDeps): { ua: UserAgent; registerer: Registerer } {
  const ua = new UserAgent(buildUserAgentOptions(b, d));
  const registerer = new Registerer(ua, REGISTERER_OPTIONS); // re-REGISTER at 150 s; tolerates a 60 s throttled wake
  return { ua, registerer };
}

/** DTMF options: RFC 4733 through the sender's DTMF, never SIP INFO */
export const DTMF_OPTIONS = { duration: 100, interToneGap: 70 } as const;
